/**
 * PlayerContext — v3.0.135 (Phase 2)
 *
 * Global playback state for the site-wide bottom bar.
 * Keeps the queue + audio element alive across route changes so a
 * user can start a playlist on /playlist/:id and keep listening
 * while browsing /dashboard, /library, /create-song, etc.
 *
 * Shape:
 *   queue: Song[]     ordered list currently loaded
 *   queueTitle: str   playlist/context name shown in the bar
 *   playlistId: str?  optional deep-link back to detail page
 *   currentIndex: number
 *   playing: bool
 *   shuffle: bool
 *   repeatMode: 'off' | 'all' | 'one'
 *   volume: number in [0,1]
 *   currentTime, duration: number (updated from <audio>)
 *
 * Song contract:
 *   { song_id, title, audio_url, image_url, duration?, locked?: bool }
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { toast } from 'sonner';

const PlayerContext = createContext(null);

const LS_VOLUME = 'wim_player_volume';
const LS_SHUFFLE = 'wim_player_shuffle';
const LS_REPEAT = 'wim_player_repeat';

const readLS = (k, fallback) => {
  try { const v = localStorage.getItem(k); return v == null ? fallback : v; }
  catch { return fallback; }
};
const writeLS = (k, v) => {
  try { localStorage.setItem(k, String(v)); } catch { /* ignore */ }
};

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [queueTitle, setQueueTitle] = useState('');
  const [playlistId, setPlaylistId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [shuffle, setShuffleState] = useState(readLS(LS_SHUFFLE, 'false') === 'true');
  // v3.0.148 — Default repeat is now 'all' so playlists loop end-to-start
  // out of the box. Users who explicitly set 'off' will still get 'off'
  // (localStorage is read first).
  const [repeatMode, setRepeatModeState] = useState(readLS(LS_REPEAT, 'all'));
  const [volume, setVolumeState] = useState(() => {
    const v = parseFloat(readLS(LS_VOLUME, '0.9'));
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.9;
  });
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [barVisible, setBarVisible] = useState(true);
  // v3.0.148 — When repeat=off and the playlist ends, we show a friendly
  // endcard (Play again / Share / Dismiss) instead of just a tiny toast.
  const [endcardVisible, setEndcardVisible] = useState(false);
  // v3.0.148 — Track whether the current advance was auto (natural end)
  // so we know to fire a subtle "Now playing: ..." toast. User-initiated
  // clicks (playAtIndex / next / prev button) don't need to be told.
  const autoAdvanceRef = useRef(false);

  // v3.0.147 — Intent flag that survives the transient `pause` event
  // fired by HTML5 media elements on natural end AND on `.load()`.
  // Without this, auto-advance after a song ends drops `playing` to
  // false via the audio element's onPause → setPlaying(false), and the
  // src-change effect then skips `.play()`.
  const shouldPlayRef = useRef(false);

  // Shuffled-order helper: keep a stable random permutation of indexes.
  const shuffleOrderRef = useRef([]);
  const rebuildShuffleOrder = useCallback((size, keepIndex) => {
    const rest = Array.from({ length: size }, (_, i) => i).filter((i) => i !== keepIndex);
    for (let i = rest.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    shuffleOrderRef.current = keepIndex != null ? [keepIndex, ...rest] : rest;
  }, []);

  const current = queue[currentIndex] || null;

  // Load new source when the current song changes.
  useEffect(() => {
    if (!audioRef.current || !current) return;
    if (current.locked || !current.audio_url) {
      // Skip locked/unplayable songs — try next playable, but never
      // land back on the same index (that would loop forever when the
      // rest of the queue is locked).
      const step = (i) => (i + 1) % Math.max(1, queue.length);
      let cursor = currentIndex;
      for (let n = 0; n < queue.length; n += 1) {
        cursor = step(cursor);
        if (cursor === currentIndex) break;
        const s = queue[cursor];
        if (s && !s.locked && s.audio_url) {
          setCurrentIndex(cursor);
          return;
        }
      }
      // Nothing else playable — stop.
      setPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
      }
      return;
    }
    audioRef.current.src = current.audio_url;
    audioRef.current.load();
    audioRef.current.volume = volume;
    // v3.0.147 — Read intent from BOTH `playing` (initial user click)
    // AND `shouldPlayRef` (survives the transient pause event on
    // natural end / load()). Reset the ref after consuming it.
    const wantsToPlay = playing || shouldPlayRef.current;
    shouldPlayRef.current = false;
    // v3.0.148 — On auto-advance (playlist rolled to next song without
    // a user click), show a subtle "Now playing" toast so listeners
    // know what changed even when the tab is buried.
    if (autoAdvanceRef.current) {
      autoAdvanceRef.current = false;
      toast.info(`Now playing: ${current.title || 'Untitled'}`, { duration: 3000 });
    }
    if (wantsToPlay) {
      setPlaying(true);
      audioRef.current.play().catch(() => setPlaying(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.song_id, queue.length]);

  // Sync <audio>.volume with state
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const playQueue = useCallback((songs, opts = {}) => {
    const {
      startIndex = 0, title = '', playlistId: pid = null, autoplay = true,
    } = opts;
    if (!Array.isArray(songs) || songs.length === 0) return;
    setQueue(songs);
    setQueueTitle(title);
    setPlaylistId(pid);
    setCurrentIndex(Math.max(0, Math.min(startIndex, songs.length - 1)));
    setPlaying(autoplay);
    // v3.0.147 — remember the play-intent for the src-load effect
    if (autoplay) shouldPlayRef.current = true;
    setBarVisible(true);
    if (shuffle) rebuildShuffleOrder(songs.length, startIndex);
  }, [shuffle, rebuildShuffleOrder]);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el || !current) return;
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    if (queue.every((s) => s.locked)) {
      toast.error('All songs are locked — unlock at least one to play');
      return;
    }
    el.play().then(() => setPlaying(true)).catch((e) => {
      toast.error(`Couldn't play: ${e.message}`);
    });
  }, [playing, current, queue]);

  const stepIndex = useCallback((direction) => {
    if (queue.length === 0) return;
    // v3.0.147 — stepIndex is called by next()/prev()/handleEnded, all
    // of which mean "keep playing on the next track".
    shouldPlayRef.current = true;
    if (shuffle && shuffleOrderRef.current.length === queue.length) {
      const order = shuffleOrderRef.current;
      const pos = order.indexOf(currentIndex);
      const nextPos = (pos + direction + order.length) % order.length;
      setCurrentIndex(order[nextPos]);
      return;
    }
    const nextIdx = currentIndex + direction;
    if (nextIdx < 0) {
      setCurrentIndex(repeatMode === 'all' ? queue.length - 1 : 0);
      return;
    }
    if (nextIdx >= queue.length) {
      if (repeatMode === 'all') setCurrentIndex(0);
      else {
        // v3.0.148 — Instead of just a toast, show the endcard so the
        // user has an explicit "Play again" affordance.
        setPlaying(false);
        setEndcardVisible(true);
      }
      return;
    }
    setCurrentIndex(nextIdx);
  }, [queue, currentIndex, shuffle, repeatMode]);

  const next = useCallback(() => stepIndex(1), [stepIndex]);
  const prev = useCallback(() => stepIndex(-1), [stepIndex]);

  const playAtIndex = useCallback((i) => {
    if (i < 0 || i >= queue.length) return;
    // v3.0.147 — click-to-play sets both immediate flag and intent ref
    shouldPlayRef.current = true;
    setCurrentIndex(i);
    setPlaying(true);
  }, [queue]);

  const setShuffle = useCallback((v) => {
    const next = typeof v === 'boolean' ? v : !shuffle;
    setShuffleState(next);
    writeLS(LS_SHUFFLE, next);
    if (next) rebuildShuffleOrder(queue.length, currentIndex);
  }, [shuffle, queue.length, currentIndex, rebuildShuffleOrder]);

  const cycleRepeat = useCallback(() => {
    const order = ['off', 'all', 'one'];
    const next = order[(order.indexOf(repeatMode) + 1) % order.length];
    setRepeatModeState(next);
    writeLS(LS_REPEAT, next);
  }, [repeatMode]);

  const setVolume = useCallback((v) => {
    const clamped = Math.min(1, Math.max(0, v));
    setVolumeState(clamped);
    writeLS(LS_VOLUME, clamped);
  }, []);

  const seek = useCallback((t) => {
    if (audioRef.current) audioRef.current.currentTime = t;
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
    setQueue([]);
    setQueueTitle('');
    setPlaylistId(null);
    setCurrentIndex(0);
    setCurrentTime(0);
    setDuration(0);
    setBarVisible(false);
  }, []);

  const hideBar = useCallback(() => setBarVisible(false), []);

  // v3.0.148 — Endcard actions
  const dismissEndcard = useCallback(() => setEndcardVisible(false), []);

  const replayFromStart = useCallback(() => {
    if (queue.length === 0) return;
    setEndcardVisible(false);
    shouldPlayRef.current = true;
    setCurrentIndex(0);
    setPlaying(true);
  }, [queue.length]);

  const handleEnded = useCallback(() => {
    if (repeatMode === 'one') {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => setPlaying(false));
      }
      return;
    }
    // v3.0.148 — Mark this as an auto-advance so the src-change effect
    // fires the "Now playing: ..." toast on the next track.
    autoAdvanceRef.current = true;
    next();
  }, [repeatMode, next]);

  const value = useMemo(() => ({
    // state
    queue, queueTitle, playlistId, currentIndex, current, playing,
    shuffle, repeatMode, volume, currentTime, duration, barVisible,
    endcardVisible,
    // audio element ref (for <audio> mounted globally)
    audioRef,
    // actions
    playQueue, togglePlay, next, prev, playAtIndex,
    setShuffle, cycleRepeat, setVolume, seek, stop, hideBar,
    dismissEndcard, replayFromStart,
    // internal ended handler (bound to <audio> onEnded)
    handleEnded,
    setCurrentTime, setDuration, setPlaying,
  }), [
    queue, queueTitle, playlistId, currentIndex, current, playing,
    shuffle, repeatMode, volume, currentTime, duration, barVisible,
    endcardVisible,
    playQueue, togglePlay, next, prev, playAtIndex,
    setShuffle, cycleRepeat, setVolume, seek, stop, hideBar, handleEnded,
    dismissEndcard, replayFromStart,
  ]);

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used inside <PlayerProvider>');
  return ctx;
}
